import React from "react";
import styled from "styled-components";
import Button from "@ingka/button";
import InputField from "@ingka/input-field";
import Select, { Option } from "@ingka/select";
import { SkapaToast } from "../../SkapaToast";
import { InlineMessageBox } from "../../layout/InlineMessageBox";
import { useStoreSearch } from "../hooks/useStoreSearch";
import { locales } from "../../../data/constants";

const SearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-bottom: 20px;
`;

const SearchInputContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-end;
`;

const StoreTypeContainer = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-end;
  margin-bottom: 16px;
`;

const StoreTypeIndicator = styled.div<{ storeType: "regular" | "PAOP" }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  margin-left: 8px;
  color: white;
  background-color: ${(props) => (props.storeType === "regular" ? "#0058a3" : "#e75113")};
`;

const StoreInfoContainer = styled.div`
  background-color: #f5f5f5;
  padding: 16px;
  border-radius: 8px;
  margin-top: 16px;
`;

const StoreInfoTitle = styled.h3`
  margin: 0 0 12px 0;
  color: #333;
`;

const StoreInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 12px;
`;

const StoreInfoItem = styled.div`
  background-color: white;
  padding: 12px;
  border-radius: 4px;
  border-left: 3px solid #0058a3;
`;

const LocaleHeader = styled.h4`
  margin: 0 0 8px 0;
  color: #0058a3;
  font-size: 14px;
  font-weight: 600;
`;

const InfoText = styled.p`
  margin: 4px 0;
  font-size: 13px;
  color: #666;
`;

const ActionContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
  align-items: center;
`;

export const StoreSearchImport: React.FC = () => {
  const {
    searchId,
    setSearchId,
    foundStore,
    foundIn,
    selectedPrimaryLocale,
    setSelectedPrimaryLocale,
    selectedCountryId,
    setSelectedCountryId,
    storeType,
    setStoreType,
    isSearching,
    isImporting,
    errorMessage,
    successMessage,
    searchStore,
    importFoundStore,
    clearSuccess,
  } = useStoreSearch();

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      searchStore();
    }
  };

  const handlePrimaryLocaleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedPrimaryLocale(event.target.value);
  };

  const handleStoreTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setStoreType(event.target.value as "REGULAR" | "PAOP");
  };

  const handleCountryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCountryId(event.target.value);
  };

  const handleImport = () => {
    importFoundStore();
  };

  return (
    <>
      <SearchContainer>
        <h2>Import Store from IKEA Database</h2>
        <StoreTypeContainer>
          <div style={{ minWidth: "200px" }}>
            <Select id="store-type" label="Store Type" value={storeType} onChange={handleStoreTypeChange}>
              <Option id="REGULAR" name="Regular Stores" value="REGULAR" />
              <Option id="PAOP" name="Lada/PAOP" value="PAOP" />
            </Select>
          </div>

          <div style={{ flex: 1, fontSize: "14px", color: "#666", padding: "8px 0" }}>
            {storeType === "REGULAR"
              ? "Search in traditional IKEA stores with full product range"
              : "Search in specialized stores: Blue Boxes, Lada stores, and PAOP locations"}
          </div>
        </StoreTypeContainer>
        <SearchInputContainer>
          <div style={{ flex: 1 }}>
            <InputField
              id="store-search-id"
              type="text"
              label="Store ID"
              placeholder="Enter store ID (e.g., 001, 002, 003)"
              value={searchId}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchId(e.target.value)}
              onKeyPress={handleKeyPress}
            />
          </div>
          <Button type="primary" onClick={searchStore} loading={isSearching} disabled={!searchId.trim()}>
            Search Store
          </Button>
        </SearchInputContainer>

        {errorMessage && <InlineMessageBox variant="negative" title="Error" body={errorMessage} />}

        {foundStore && foundIn && (
          <StoreInfoContainer>
            <StoreInfoTitle>
              Store Found: {foundStore.storeId} - {foundStore.name}
              <StoreTypeIndicator storeType={foundIn === "REGULAR" ? "regular" : "PAOP"}>
                {foundIn === "REGULAR" ? "Regular Store" : "PAOP Store"}
              </StoreTypeIndicator>
            </StoreInfoTitle>

            <InfoText>
              <strong>Country:</strong> {foundStore.countryId}
            </InfoText>

            <InfoText>
              <strong>Status:</strong> {foundStore.status}
            </InfoText>

            <InfoText>
              <strong>Available Languages:</strong> {foundStore.locales.length} locale(s)
            </InfoText>

            <InfoText>
              <strong>Timezone:</strong> {foundStore.timezone}
            </InfoText>

            <InfoText>
              <strong>Found in:</strong>{" "}
              {foundIn === "REGULAR" ? "Regular Stores Database" : "PAOP Stores Database (Blue Box/Lada/PAOP)"}
            </InfoText>

            <StoreInfoGrid>
              {foundStore.locales.map((localeKey) => (
                <StoreInfoItem key={localeKey}>
                  <LocaleHeader>{localeKey}</LocaleHeader>
                  <InfoText>
                    <strong>Name:</strong> {foundStore.name}
                  </InfoText>
                  <InfoText>
                    <strong>Primary Locale:</strong> {foundStore.primaryLocale}
                  </InfoText>
                  <InfoText>
                    <strong>Timezone:</strong> {foundStore.timezone}
                  </InfoText>
                  <InfoText>
                    <strong>Virtual Store:</strong> {foundStore.isVirtual ? "Yes" : "No"}
                  </InfoText>
                </StoreInfoItem>
              ))}
            </StoreInfoGrid>

            <ActionContainer>
              <div style={{ minWidth: "200px" }}>
                <Select
                  id="country-selection"
                  label="Target Country"
                  hintText="Select the country to import the store to"
                  value={selectedCountryId}
                  onChange={handleCountryChange}
                >
                  {locales.map((locale) => (
                    <Option
                      key={locale.isoCode}
                      id={locale.isoCode}
                      name={`${locale.country} (${locale.isoCode})`}
                      value={locale.isoCode}
                    />
                  ))}
                </Select>
              </div>

              <div style={{ minWidth: "200px" }}>
                <Select
                  id="primary-locale"
                  label="Primary Language"
                  hintText="Select the primary language for this store"
                  value={selectedPrimaryLocale}
                  onChange={handlePrimaryLocaleChange}
                >
                  {foundStore.locales.map((localeKey) => (
                    <Option
                      key={localeKey}
                      id={localeKey}
                      name={`${localeKey} - ${foundStore.name}`}
                      value={localeKey}
                    />
                  ))}
                </Select>
              </div>

              <Button
                type="primary"
                onClick={handleImport}
                loading={isImporting}
                disabled={!selectedPrimaryLocale || !selectedCountryId}
              >
                Import Store
              </Button>
            </ActionContainer>
          </StoreInfoContainer>
        )}
      </SearchContainer>

      {successMessage && <SkapaToast text={successMessage} isOpen={true} onCloseRequest={clearSuccess} />}
    </>
  );
};
